// Phase 5: GPS Location Tracker
// Real-time location tracking for drivers - KAS Server kompatibel

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import './GPSTracker.css';

const GPSTracker = () => {
  const { user, token } = useContext(AuthContext);
  const [location, setLocation] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [accuracy, setAccuracy] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [watchId, setWatchId] = useState(null);

  // Update location on server
  const updateLocationOnServer = useCallback(async (position) => {
    if (!token || user?.role !== 'DRIVER') return;

    try {
      const response = await fetch('/api/location/update', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          is_available: isAvailable
        })
      });

      if (response.ok) {
        setLastUpdate(new Date());
        setError(null);
      } else {
        throw new Error('Fehler beim Aktualisieren der Position');
      }
    } catch (error) {
      console.error('GPS Update Fehler:', error);
      setError('Verbindungsfehler');
    }
  }, [token, user, isAvailable]);

  // Handle successful geolocation
  const handleLocationSuccess = useCallback((position) => {
    const newLocation = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      speed: position.coords.speed,
      heading: position.coords.heading,
      timestamp: new Date(position.timestamp)
    };

    setLocation(newLocation);
    setAccuracy(position.coords.accuracy);
    setSpeed(position.coords.speed);
    setError(null);

    // Update server with new location
    updateLocationOnServer(position);
  }, [updateLocationOnServer]);

  // Handle geolocation error
  const handleLocationError = useCallback((error) => {
    let errorMessage = 'GPS-Fehler';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'GPS-Berechtigung verweigert';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Position nicht verfügbar';
        break;
      case error.TIMEOUT:
        errorMessage = 'GPS-Timeout';
        break;
      default:
        errorMessage = 'Unbekannter GPS-Fehler';
    }

    setError(errorMessage);
    console.error('GPS Error:', error);
  }, []);

  // Start GPS tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('GPS nicht unterstützt');
      return;
    }

    const options = {
      enableHighAccuracy: true,
      maximumAge: 30000, // 30 seconds
      timeout: 10000 // 10 seconds
    };

    const id = navigator.geolocation.watchPosition(
      handleLocationSuccess,
      handleLocationError,
      options
    );

    setWatchId(id);
    setIsTracking(true);
    setError(null);
  }, [handleLocationSuccess, handleLocationError]);

  // Stop GPS tracking
  const stopTracking = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setIsTracking(false);
  }, [watchId]);

  // Toggle availability status
  const toggleAvailability = async () => {
    if (!token || user?.role !== 'DRIVER') return;

    try {
      const newStatus = !isAvailable;
      
      const response = await fetch('/api/location/availability', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_available: newStatus
        })
      });

      if (response.ok) {
        setIsAvailable(newStatus);
        
        // If going offline, stop tracking
        if (!newStatus) {
          stopTracking();
        } else if (!isTracking) {
          // If going online and not tracking, start tracking
          startTracking();
        }
      } else {
        throw new Error('Fehler beim Ändern des Status');
      }
    } catch (error) {
      console.error('Availability toggle error:', error);
      setError('Status konnte nicht geändert werden');
    }
  };

  // Format coordinates
  const formatCoordinate = (coord, decimals = 6) => {
    return coord ? coord.toFixed(decimals) : '---';
  };

  // Format speed
  const formatSpeed = (speed) => {
    if (speed === null || speed === undefined) return '---';
    const kmh = (speed * 3.6).toFixed(1);
    return `${kmh} km/h`;
  };

  // Format time since last update
  const formatLastUpdate = (date) => {
    if (!date) return '---';
    const diff = Math.floor((new Date() - date) / 1000);
    if (diff < 60) return `vor ${diff}s`;
    if (diff < 3600) return `vor ${Math.floor(diff / 60)}m`;
    return date.toLocaleTimeString('de-DE');
  };

  // Auto-start tracking for drivers when component mounts
  useEffect(() => {
    if (user?.role === 'DRIVER' && isAvailable && !isTracking) {
      startTracking();
    }

    // Cleanup on unmount
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [user, isAvailable, isTracking, startTracking, watchId]);

  // Only show for drivers
  if (!user || user.role !== 'DRIVER') {
    return null;
  }

  return (
    <div className="gps-tracker">
      <div className="gps-header">
        <div className="gps-status">
          <span className={`status-indicator ${isTracking ? 'active' : 'inactive'}`}>
            {isTracking ? '📍' : '📍'}
          </span>
          <span className="status-text">
            {isTracking ? 'GPS Aktiv' : 'GPS Inaktiv'}
          </span>
        </div>
        
        <div className="availability-toggle">
          <button 
            className={`availability-btn ${isAvailable ? 'available' : 'unavailable'}`}
            onClick={toggleAvailability}
          >
            {isAvailable ? '🟢 Verfügbar' : '🔴 Nicht verfügbar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="gps-error">
          <span>⚠️ {error}</span>
          <button onClick={startTracking} className="retry-btn">
            Erneut versuchen
          </button>
        </div>
      )}

      {location && (
        <div className="gps-info">
          <div className="location-grid">
            <div className="location-item">
              <label>Breitengrad:</label>
              <span>{formatCoordinate(location.latitude)}</span>
            </div>
            <div className="location-item">
              <label>Längengrad:</label>
              <span>{formatCoordinate(location.longitude)}</span>
            </div>
            <div className="location-item">
              <label>Genauigkeit:</label>
              <span>{accuracy ? `±${Math.round(accuracy)}m` : '---'}</span>
            </div>
            <div className="location-item">
              <label>Geschwindigkeit:</label>
              <span>{formatSpeed(speed)}</span>
            </div>
          </div>
          
          <div className="last-update">
            <span>Letzte Aktualisierung: {formatLastUpdate(lastUpdate)}</span>
          </div>
        </div>
      )}

      <div className="gps-controls">
        {!isTracking ? (
          <button onClick={startTracking} className="start-tracking">
            📍 GPS starten
          </button>
        ) : (
          <button onClick={stopTracking} className="stop-tracking">
            ⏹️ GPS stoppen
          </button>
        )}
      </div>

      {isTracking && (
        <div className="tracking-info">
          <div className="tracking-indicator">
            <div className="pulse-dot"></div>
            <span>Position wird verfolgt...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSTracker;
